// "Remember me on this device" storage for the auth forms.
//
// Two mechanisms, split by who owns the secret:
//   - The EMAIL is persisted here in localStorage (readable, non-secret) so a
//     returning visitor whose session expired finds it prefilled instead of a
//     blank form.
//   - The PASSWORD is never persisted by us. `saveDeviceCredential` hands the
//     entered credentials to the BROWSER's own password manager via the
//     Credential Management API, so a changed password updates the saved entry
//     and stale autofill self-heals on the next successful sign-in.

import { readStorage, removeStorage, writeStorage } from './browser-storage';

const STORAGE_KEY = 'leanin.rememberedLogin';

export type RememberedLogin = {
  readonly email: string;
};

export function readRememberedLogin(): RememberedLogin | null {
  const raw = readStorage('local', STORAGE_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    if (!(err instanceof SyntaxError)) throw err;
    return null;
  }
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    'email' in parsed &&
    typeof (parsed as { email: unknown }).email === 'string' &&
    (parsed as { email: string }).email !== ''
  ) {
    return { email: (parsed as { email: string }).email };
  }
  return null;
}

// An unpersisted write means the checkbox simply won't stick.
export function writeRememberedLogin(login: RememberedLogin): void {
  writeStorage('local', STORAGE_KEY, JSON.stringify(login));
}

export function clearRememberedLogin(): void {
  removeStorage('local', STORAGE_KEY);
}

// Boundary type for the Credential Management API's `PasswordCredential`,
// which is Chromium-only and absent from TS's DOM lib. Typed here as the slice
// we use; on Safari/Firefox the global is undefined and we no-op.
type PasswordCredentialCtor = new (init: {
  id: string;
  password: string;
}) => Credential;

// Asks the BROWSER's password manager to save/update the credential for this
// device — the same secure store its native "Save password?" prompt writes to.
// This is what keeps a changed password from getting stuck: our custom Clerk
// form submits via JS, so the browser's own update heuristic fires unreliably;
// storing explicitly after a verified sign-in makes the saved password track
// the one that actually worked. We never persist the password ourselves.
//
// Callers gate this behind "Remember me" (explicit consent). Best-effort: any
// failure — unsupported browser, insecure context, user dismissal — is
// swallowed so it can never block the sign-in it follows.
export async function saveDeviceCredential(
  email: string,
  password: string,
): Promise<void> {
  if (typeof window === 'undefined' || email === '' || password === '') return;
  if (!('PasswordCredential' in window)) return;
  // Browser-API boundary: narrow the untyped global to the constructor slice.
  const Ctor = (window as unknown as { PasswordCredential: PasswordCredentialCtor })
    .PasswordCredential;
  try {
    await navigator.credentials.store(new Ctor({ id: email, password }));
  } catch {
    // Never block sign-in on a credential-store failure.
  }
}
